import React, { useContext } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { SpinnerIcon } from 'tombac-icons';
import { propStyling } from '../../shared/propStyling';
import { tombac } from '../../shared/tombac';
import { ButtonGroupContext } from './ButtonGroupContext';
import { createRipple } from './createRipple';
const baseStyles = css(["all:initial;align-items:center;border-radius:", ";box-sizing:border-box;cursor:", ";display:inline-flex;flex-direction:column;justify-content:flex-start;overflow:hidden;position:relative;text-align:center;transition:0.1s ease-out;transition-property:background-color,border-color,box-shadow;white-space:nowrap;will-change:transform;vertical-align:middle;&:focus,&:focus-visible{box-shadow:0 0 0 ", " ", ";z-index:1;}&:focus:not(:focus-visible){box-shadow:initial;z-index:initial;}&::-moz-focus-inner{border:0;}"], _ref => {
  let {
    shape
  } = _ref;
  return shape === 'circle' ? '100%' : tombac.unit(1);
}, _ref2 => {
  let {
    disabled
  } = _ref2;
  return disabled ? 'default' : 'pointer';
}, tombac.unit(4), tombac.color('accent', 200));
const disabledStyles = css(["background-color:", ";color:", ";", ""], tombac.color('neutral', 400), tombac.alpha('neutral', 24), _ref3 => {
  let {
    variant
  } = _ref3;
  return variant === 'ghost' && css(["border:", " solid ", ";"], tombac.unit(1), tombac.color('neutral', 400));
});

// Animation is synchronized with setTimeout value in createRipple.
const rippleKeyframes = keyframes(["50%{transform:scale(3);opacity:1;}60%{opacity:1;}70%{transform:scale(3);opacity:0;}100%{transform:scale(3);opacity:0;}"]);
const rippleStyles = css([".TombacButton__ripple{overflow:hidden;position:absolute;border-radius:50%;pointer-events:none;animation:", " 1s ease-out;transform:scale(0);}.TombacButton__ripple--active{animation:", " 1s ease-out;}"], rippleKeyframes, rippleKeyframes);
const sizeStyles = tombac.variant('size', {
  xl: css(["", " height:", ";border-radius:", ";min-width:", ";padding:0 ", ";"], tombac.altText({
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5
  }), tombac.space(7), _ref4 => {
    let {
      shape
    } = _ref4;
    return !shape && tombac.space(3.5);
  }, tombac.space(7), _ref5 => {
    let {
      append,
      busy,
      prepend,
      shape,
      square
    } = _ref5;
    return tombac.space(square || shape ? 0 : append || busy || prepend ? 3 : 3.5);
  }),
  l: css(["", " height:", ";border-radius:", ";min-width:", ";padding:0 ", ";"], tombac.altText({
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.5
  }), tombac.space(6), _ref6 => {
    let {
      shape
    } = _ref6;
    return !shape && tombac.space(3);
  }, tombac.space(6), _ref7 => {
    let {
      append,
      busy,
      prepend,
      shape,
      square
    } = _ref7;
    return tombac.space(square || shape ? 0 : append || busy || prepend ? 2.5 : 3);
  }),
  m: css(["", " height:", ";border-radius:", ";min-width:", ";padding:0 ", ";"], tombac.altText({
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.5
  }), tombac.space(5), _ref8 => {
    let {
      shape
    } = _ref8;
    return !shape && tombac.space(2.5);
  }, tombac.space(5), _ref9 => {
    let {
      append,
      busy,
      prepend,
      shape,
      square
    } = _ref9;
    return tombac.space(square || shape ? 0 : append || busy || prepend ? 2 : 2.5);
  }),
  s: css(["", " height:", ";border-radius:", ";min-width:", ";padding:0 ", ";"], tombac.altText({
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.5
  }), tombac.space(4), _ref10 => {
    let {
      shape
    } = _ref10;
    return !shape && tombac.space(2);
  }, tombac.space(4), _ref11 => {
    let {
      append,
      busy,
      prepend,
      shape,
      square
    } = _ref11;
    return tombac.space(square || shape ? 0 : append || busy || prepend ? 1.5 : 2);
  }),
  xs: css(["", " height:", ";border-radius:", ";min-width:", ";padding:0 ", ";"], tombac.altText({
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5
  }), tombac.space(3), _ref12 => {
    let {
      shape
    } = _ref12;
    return !shape && tombac.space(1.5);
  }, tombac.space(3), _ref13 => {
    let {
      append,
      busy,
      prepend,
      shape,
      square
    } = _ref13;
    return tombac.space(square || shape ? 0 : append || busy || prepend ? 1 : 1.5);
  })
});
const variantStyles = tombac.variant('variant', {
  accent: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('accent'), tombac.color.white, tombac.color('accent'), tombac.color('accent', 600)),
  alert: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:focus,&:focus-visible{box-shadow:0 0 0 ", " ", ";}&:focus:not(:focus-visible){box-shadow:initial;}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('alert'), tombac.color.white, tombac.color('alert'), tombac.unit(4), tombac.color('alert', 200), tombac.color('alert', 600)),
  danger: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:focus,&:focus-visible{box-shadow:0 0 0 ", " ", ";}&:focus:not(:focus-visible){box-shadow:initial;}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('danger'), tombac.color.white, tombac.color('danger'), tombac.unit(4), tombac.color('danger', 200), tombac.color('danger', 600)),
  flat: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color.white, tombac.color.black, tombac.color.white, tombac.color('neutral', 300)),
  ghost: css(["background-color:", ";border:", " solid ", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:focus,&:focus-visible{border-color:", ";}&:focus:not(:focus-visible){border-color:", ";}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";border-color:", ";}"], tombac.color.white, tombac.unit(1), tombac.color('neutral', 400), tombac.color.black, tombac.color.white, tombac.color.white, tombac.color('neutral', 400), tombac.color('neutral', 300), tombac.color('neutral', 300)),
  primary: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:focus,&:focus-visible{box-shadow:0 0 0 ", " ", ";}&:focus:not(:focus-visible){box-shadow:initial;}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('primary'), tombac.color.white, tombac.color('primary'), tombac.unit(4), tombac.color('primary', 200), tombac.color('primary', 600)),
  secondary: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('neutral', 300), tombac.color.black, tombac.color('neutral', 300), tombac.color('neutral', 400)),
  success: css(["background-color:", ";color:", ";& > .TombacButton__ripple{background-color:", ";}&:focus,&:focus-visible{box-shadow:0 0 0 ", " ", ";}&:focus:not(:focus-visible){box-shadow:initial;}&:not([disabled]):active,&[data-ripple='active']{background-color:", ";}"], tombac.color('success'), tombac.color.white, tombac.color('success'), tombac.unit(4), tombac.color('success', 200), tombac.color('success', 600))
});
const Label = styled.span.withConfig({
  displayName: "ButtonLegacy__Label"
})(["margin:", ";"], _ref14 => {
  let {
    append,
    busy,
    prepend,
    size
  } = _ref14;
  return tombac.space(append || busy || prepend ? size === 'xs' ? 0.5 : size === 's' ? 0.75 : 1 : 0);
});
const StyledSpinner = styled(props => /*#__PURE__*/React.createElement(SpinnerIcon, props)).withConfig({
  displayName: "ButtonLegacy__StyledSpinner"
})(["height:", ";width:", ";"], _ref15 => {
  let {
    size
  } = _ref15;
  return tombac.unit(size === 'xs' ? 17 : 20);
}, _ref16 => {
  let {
    size
  } = _ref16;
  return tombac.unit(size === 'xs' ? 20 : 24);
});
export const ButtonLegacy = styled.button.attrs(_ref17 => {
  var _ref18, _ref19, _groupContext$size, _theme$settings;
  let {
    append,
    appendedHoverAnimation,
    as,
    busy,
    children,
    disabled,
    hoverAnimation = 'up',
    prepend,
    prependedHoverAnimation,
    size,
    theme,
    variant,
    onClick
  } = _ref17;
  const groupContext = useContext(ButtonGroupContext);
  const buttonSize = (_ref18 = (_ref19 = (_groupContext$size = groupContext.size) !== null && _groupContext$size !== void 0 ? _groupContext$size : size) !== null && _ref19 !== void 0 ? _ref19 : (_theme$settings = theme.settings) === null || _theme$settings === void 0 ? void 0 : _theme$settings.buttonSize) !== null && _ref18 !== void 0 ? _ref18 : 'm';
  const content = append || prepend ? /*#__PURE__*/React.createElement(React.Fragment, null, prepend && /*#__PURE__*/React.createElement(React.Fragment, null, busy ? /*#__PURE__*/React.createElement(StyledSpinner, {
    size: size,
    spin: true
  }) : prependedHoverAnimation ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AnimatedAddon, {
    className: `TombacButtonAddon TombacButtonAddon--prepended TombacButtonAddon--animation-${prependedHoverAnimation}`
  }, prepend), /*#__PURE__*/React.createElement(AnimatedAddon, {
    "aria-hidden": true,
    className: `TombacButtonAddon TombacButtonAddon--prepended TombacButtonAddon--animation-${prependedHoverAnimation}`,
    position: "prepended"
  }, prepend)) : prepend, ' '), children && /*#__PURE__*/React.createElement(Label, {
    append,
    busy,
    prepend,
    size
  }, children), append && /*#__PURE__*/React.createElement(React.Fragment, null, ' ', busy && !prepend ? /*#__PURE__*/React.createElement(StyledSpinner, {
    size: size,
    spin: true
  }) : appendedHoverAnimation ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AnimatedAddon, {
    className: `TombacButtonAddon TombacButtonAddon--appended TombacButtonAddon--animation-${appendedHoverAnimation}`
  }, append), /*#__PURE__*/React.createElement(AnimatedAddon, {
    "aria-hidden": true,
    className: `TombacButtonAddon TombacButtonAddon--appended TombacButtonAddon--animation-${appendedHoverAnimation}`,
    position: "appended"
  }, append)) : append)) : busy ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledSpinner, {
    size: size,
    spin: true
  }), ' ', /*#__PURE__*/React.createElement(Label, {
    append,
    busy,
    prepend,
    size
  }, children)) : children;
  return {
    as: as === 'a' && disabled ? 'button' : as,
    children: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledContent, null, content), /*#__PURE__*/React.createElement(StyledContent, {
      "aria-hidden": true
    }, content)),
    className: `TombacButton TombacButton--variant-${variant} TombacButton--size-${buttonSize} TombacButton--hover-animation-${hoverAnimation}`,
    disabled: disabled || busy,
    size: buttonSize,
    onClick: createRipple(onClick)
  };
}).withConfig({
  displayName: "ButtonLegacy"
})(["", " ", " ", " ", " ", ""], baseStyles, sizeStyles, rippleStyles, _ref20 => {
  let {
    busy,
    disabled
  } = _ref20;
  return disabled && !busy ? disabledStyles : variantStyles;
}, propStyling);
ButtonLegacy.defaultProps = {
  variant: 'secondary'
};
const easeOutExp = 'cubic-bezier(0.16, 1, 0.3, 1)';
const easeOutQuad = 'cubic-bezier(0.45, 0, 0.55, 1)';
const contentAnimDuration = '0.75s';
const hoverUp = keyframes(["0%{opacity:1;}40%,100%{opacity:0;}"]);
const hoverUpHidden = keyframes(["0%{opacity:0;}40%,100%{opacity:1;}"]);
const hoverForward = keyframes(["0%{opacity:1;transform:translateX(0);}25%{opacity:0;}100%{opacity:0;transform:translateX(100%);}"]);
const hoverForwardHidden = keyframes(["0%{opacity:0;transform:translateX(-100%);}25%{opacity:0;}50%{opacity:1;}100%{opacity:1;transform:translateX(0);}"]);
const hoverBackward = keyframes(["0%{opacity:1;transform:translateX(0);}25%{opacity:0;}100%{opacity:0;transform:translateX(-100%);}"]);
const hoverBackwardHidden = keyframes(["0%{opacity:0;transform:translateX(100%);}25%{opacity:0;}50%{opacity:1;}100%{opacity:1;transform:translateX(0);}"]);
const AnimatedAddon = styled.div.withConfig({
  displayName: "ButtonLegacy__AnimatedAddon"
})(["&[aria-hidden]{left:", ";opacity:0;position:absolute;right:", ";}", ":not([disabled]):hover &.TombacButtonAddon--animation-forward:not([aria-hidden]){animation:", " 1s ", " ", " infinite;}", ":not([disabled]):hover &.TombacButtonAddon--animation-forward[aria-hidden]{animation:", " 1s ", " ", " infinite;}", ":not([disabled]):hover &.TombacButtonAddon--animation-backward:not([aria-hidden]){animation:", " 1s ", " ", " infinite;}", ":not([disabled]):hover &.TombacButtonAddon--animation-backward[aria-hidden]{animation:", " 1s ", " ", " infinite;}"], _ref21 => {
  let {
    position
  } = _ref21;
  return position === 'prepended' ? 0 : undefined;
}, _ref22 => {
  let {
    position
  } = _ref22;
  return position === 'appended' ? 0 : undefined;
}, ButtonLegacy, hoverForward, easeOutExp, contentAnimDuration, ButtonLegacy, hoverForwardHidden, easeOutExp, contentAnimDuration, ButtonLegacy, hoverBackward, easeOutExp, contentAnimDuration, ButtonLegacy, hoverBackwardHidden, easeOutExp, contentAnimDuration);
const StyledContent = styled.span.withConfig({
  displayName: "ButtonLegacy__StyledContent"
})(["align-items:center;display:inline-flex;flex:none;height:100%;position:relative;z-index:1;", " > &[aria-hidden]{opacity:0;top:-60%;}", ":not([disabled]):hover > &{transform:translateY(-40%);}", ":not([disabled]):hover > &:not([aria-hidden]){animation:", " ", " ", " forwards;transition:transform ", " ", ";}", ":not([disabled]):hover > &[aria-hidden]{animation:", " ", " ", " forwards;transition:transform ", " ", ";}", " > &[aria-hidden],", " > &[aria-hidden]{opacity:0;position:absolute;}", ":not([disabled]):hover > &:not([aria-hidden]){animation:", " 1s ", " infinite;}", ":not([disabled]):hover > &[aria-hidden]{animation:", " 1s ", " infinite;}", ":not([disabled]):hover > &:not([aria-hidden]){animation:", " 1s ", " infinite;}", ":not([disabled]):hover > &[aria-hidden]{animation:", " 1s ", " infinite;}"], `${ButtonLegacy}.TombacButton--hover-animation-up`, `${ButtonLegacy}.TombacButton--hover-animation-up`, `${ButtonLegacy}.TombacButton--hover-animation-up`, hoverUp, contentAnimDuration, easeOutQuad, contentAnimDuration, easeOutExp, `${ButtonLegacy}.TombacButton--hover-animation-up`, hoverUpHidden, contentAnimDuration, easeOutQuad, contentAnimDuration, easeOutExp, `${ButtonLegacy}.TombacButton--hover-animation-forward`, `${ButtonLegacy}.TombacButton--hover-animation-backward`, `${ButtonLegacy}.TombacButton--hover-animation-forward`, hoverForward, easeOutExp, `${ButtonLegacy}.TombacButton--hover-animation-forward`, hoverForwardHidden, easeOutExp, `${ButtonLegacy}.TombacButton--hover-animation-backward`, hoverBackward, easeOutExp, `${ButtonLegacy}.TombacButton--hover-animation-backward`, hoverBackwardHidden, easeOutExp);
//# sourceMappingURL=ButtonLegacy.js.map